import { parse } from 'date-fns';

/**
 * Parse a "plain date" value into a `Date` anchored at local midnight.
 *
 * A plain date string in `yyyy-MM-dd` format has no time-of-day and no
 * timezone — it represents a calendar day. `new Date('yyyy-MM-dd')` parses
 * such strings as UTC midnight, which renders as the previous day in any
 * timezone west of UTC's no-DST baseline (e.g. `Europe/Warsaw` shows
 * `2026-05-10 22:00` for `'2026-05-11'`). This helper avoids that shift by
 * parsing the date components into a `Date` at local midnight instead.
 *
 * Inputs other than `yyyy-MM-dd` strings are returned as-is for `Date`,
 * delegated to `new Date()` for other strings (e.g. ISO datetimes with an
 * explicit offset, where local-vs-UTC ambiguity does not exist), and passed
 * through for everything else.
 */
export const parsePlainDate = (value: unknown): Date | undefined => {
  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'string') {
    if (value === '') {
      return undefined;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return parse(value, 'yyyy-MM-dd', new Date());
    }

    return new Date(value);
  }

  return undefined;
};
